import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  Cable, Plug, ToggleLeft, LayoutGrid, Lightbulb, Zap, Shield,
  Plus, Minus, Trash2, History, FileText, Sun, Moon, Calculator,
  Download, ChevronLeft, Clock, Euro
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Format currency in European format
const formatCurrency = (value) => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2
  }).format(value);
};

// Format date
const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

// Category icons mapping
const categoryIcons = {
  cables: Cable,
  sockets: Plug,
  switches: ToggleLeft,
  panels: LayoutGrid,
  bulbs: Lightbulb,
  breakers: Zap,
  rcd: Shield
};

// Category colors
const categoryColors = {
  cables: "bg-blue-500",
  sockets: "bg-green-500",
  switches: "bg-amber-500",
  panels: "bg-purple-500",
  bulbs: "bg-yellow-500",
  breakers: "bg-red-500",
  rcd: "bg-cyan-500"
};

function App() {
  const [darkMode, setDarkMode] = useState(false);
  const [view, setView] = useState("calculator"); // calculator, history
  const [categories, setCategories] = useState({});
  const [materials, setMaterials] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [estimateItems, setEstimateItems] = useState([]);
  const [laborRate, setLaborRate] = useState(45);
  const [estimates, setEstimates] = useState([]);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [estimateName, setEstimateName] = useState("");
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [loading, setLoading] = useState(true);

  // Toggle dark mode
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [categoriesRes, materialsRes, settingsRes] = await Promise.all([
          axios.get(`${API}/materials/categories`),
          axios.get(`${API}/materials`),
          axios.get(`${API}/settings`)
        ]);
        setCategories(categoriesRes.data);
        setMaterials(materialsRes.data);
        setLaborRate(settingsRes.data.labor_rate);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Ошибка загрузки данных");
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Fetch estimates history
  const fetchEstimates = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/estimates`);
      setEstimates(response.data);
    } catch (error) {
      console.error("Error fetching estimates:", error);
    }
  }, []);

  useEffect(() => {
    if (view === "history") {
      fetchEstimates();
    }
  }, [view, fetchEstimates]);

  // Calculate totals
  const totals = estimateItems.reduce((acc, item) => ({
    materials: acc.materials + item.material_cost,
    labor: acc.labor + item.labor_cost,
    total: acc.total + item.total_cost
  }), { materials: 0, labor: 0, total: 0 });

  // Get materials for selected category
  const categoryMaterials = selectedCategory 
    ? materials.filter(m => m.category === selectedCategory)
    : [];

  // Add item to estimate
  const addItem = (material) => {
    const existingIndex = estimateItems.findIndex(item => item.material_id === material.id);
    
    if (existingIndex >= 0) {
      updateQuantity(existingIndex, estimateItems[existingIndex].quantity + 1);
    } else {
      const laborHours = material.labor_time * 1;
      const laborCost = laborHours * laborRate;
      const materialCost = material.price * 1;
      
      const newItem = {
        material_id: material.id,
        material_name: material.name,
        category: material.category,
        quantity: 1,
        unit: material.unit,
        unit_price: material.price,
        material_cost: materialCost,
        labor_hours: laborHours,
        labor_cost: laborCost,
        total_cost: materialCost + laborCost
      };
      
      setEstimateItems([...estimateItems, newItem]);
      toast.success(`${material.name} добавлено`);
    }
  };

  // Update item quantity
  const updateQuantity = (index, newQuantity) => {
    if (newQuantity < 0) return;
    
    if (newQuantity === 0) {
      removeItem(index);
      return;
    }

    const updatedItems = [...estimateItems];
    const item = updatedItems[index];
    const material = materials.find(m => m.id === item.material_id);
    
    if (material) {
      const laborHours = material.labor_time * newQuantity;
      const laborCost = laborHours * laborRate;
      const materialCost = material.price * newQuantity;
      
      updatedItems[index] = {
        ...item,
        quantity: newQuantity,
        material_cost: materialCost,
        labor_hours: laborHours,
        labor_cost: laborCost,
        total_cost: materialCost + laborCost
      };
      
      setEstimateItems(updatedItems);
    }
  };

  // Remove item
  const removeItem = (index) => {
    const item = estimateItems[index];
    setEstimateItems(estimateItems.filter((_, i) => i !== index));
    toast.info(`${item.material_name} удалено`);
  };

  // Clear all items
  const clearEstimate = () => {
    setEstimateItems([]);
    toast.info("Смета очищена");
  };

  // Save estimate
  const saveEstimate = async () => {
    if (!estimateName.trim()) {
      toast.error("Введите название сметы");
      return;
    }
    
    if (estimateItems.length === 0) {
      toast.error("Добавьте материалы в смету");
      return;
    }

    try {
      await axios.post(`${API}/estimates`, {
        name: estimateName,
        items: estimateItems,
        labor_rate: laborRate,
        total_materials: totals.materials,
        total_labor: totals.labor,
        grand_total: totals.total
      });
      
      toast.success("Смета сохранена");
      setSaveDialogOpen(false);
      setEstimateName("");
    } catch (error) {
      console.error("Error saving estimate:", error);
      toast.error("Ошибка сохранения");
    }
  };

  // Load estimate from history
  const loadEstimate = (estimate) => {
    setEstimateItems(estimate.items);
    setLaborRate(estimate.labor_rate);
    setSelectedEstimate(null);
    setView("calculator");
    toast.success("Смета загружена");
  };

  // Delete estimate
  const deleteEstimate = async (estimateId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/estimates/${estimateId}`);
      setEstimates(estimates.filter(e => e.id !== estimateId));
      toast.success("Смета удалена");
    } catch (error) {
      console.error("Error deleting estimate:", error);
      toast.error("Ошибка удаления");
    }
  };

  // Export to PDF (print)
  const exportPDF = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center blueprint-bg">
        <div className="text-center">
          <Calculator className="w-12 h-12 text-primary mx-auto mb-4 animate-pulse" />
          <p className="text-muted-foreground">Загрузка...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen blueprint-bg" data-testid="app-container">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 no-print">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-primary flex items-center justify-center">
              <Zap className="w-6 h-6 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-xl font-black tracking-tight" data-testid="app-title">ЭЛЕКТРОКАЛЬКУЛЯТОР</h1>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Расчёт электромонтажа</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant={view === "calculator" ? "default" : "outline"}
              size="sm"
              onClick={() => setView("calculator")}
              className="uppercase tracking-wider text-xs font-bold"
              data-testid="calculator-tab"
            >
              <Calculator className="w-4 h-4 mr-1" />
              Калькулятор
            </Button>
            <Button
              variant={view === "history" ? "default" : "outline"}
              size="sm"
              onClick={() => setView("history")}
              className="uppercase tracking-wider text-xs font-bold"
              data-testid="history-tab"
            >
              <History className="w-4 h-4 mr-1" />
              История
            </Button>
            <Separator orientation="vertical" className="h-6 mx-2" />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setDarkMode(!darkMode)}
              className="dark-toggle"
              data-testid="dark-mode-toggle"
            >
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6">
        {view === "calculator" ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Categories & Materials */}
            <div className="lg:col-span-2 space-y-6">
              {/* Labor Rate Setting */}
              <Card className="border-2 rounded-sm" data-testid="labor-rate-card">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Clock className="w-5 h-5 text-primary" />
                      <span className="font-bold uppercase tracking-wider text-sm">Ставка за работу</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={laborRate}
                        onChange={(e) => setLaborRate(parseFloat(e.target.value) || 0)}
                        className="w-24 font-mono text-lg font-bold text-right rounded-sm"
                        data-testid="labor-rate-input"
                      />
                      <span className="text-muted-foreground font-mono">€/час</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Categories */}
              {!selectedCategory ? (
                <div data-testid="categories-grid">
                  <h2 className="text-2xl font-bold mb-4 tracking-tight">Выберите категорию</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {Object.entries(categories).map(([key, category]) => {
                      const IconComponent = categoryIcons[key];
                      const itemCount = materials.filter(m => m.category === key).length;
                      
                      return (
                        <Card 
                          key={key}
                          className="category-card cursor-pointer border-2 hover:border-primary/50 rounded-sm"
                          onClick={() => setSelectedCategory(key)}
                          data-testid={`category-${key}`}
                        >
                          <CardContent className="p-4 text-center">
                            <div className={`w-12 h-12 mx-auto mb-3 rounded-sm ${categoryColors[key]} flex items-center justify-center`}>
                              <IconComponent className="w-6 h-6 text-white" />
                            </div>
                            <h3 className="font-bold text-sm mb-1">{category.name}</h3>
                            <p className="text-xs text-muted-foreground">{itemCount} позиций</p>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              ) : (
                /* Materials List */
                <div data-testid="materials-list">
                  <div className="flex items-center gap-4 mb-4">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setSelectedCategory(null)}
                      className="uppercase tracking-wider text-xs font-bold"
                      data-testid="back-to-categories"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Назад
                    </Button>
                    <h2 className="text-2xl font-bold tracking-tight">
                      {categories[selectedCategory]?.name}
                    </h2>
                    <Badge variant="secondary" className="font-mono">
                      {categoryMaterials.length} позиций
                    </Badge>
                  </div>
                  
                  <Card className="border-2 rounded-sm">
                    <ScrollArea className="h-[400px]">
                      <div className="divide-y">
                        {categoryMaterials.map((material) => {
                          const inEstimate = estimateItems.find(item => item.material_id === material.id);
                          
                          return (
                            <div 
                              key={material.id}
                              className="material-item p-4 flex items-center justify-between"
                              data-testid={`material-${material.id}`}
                            >
                              <div className="flex-1">
                                <h4 className="font-medium">{material.name}</h4>
                                <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                                  <span className="font-mono font-bold text-primary">
                                    {formatCurrency(material.price)}/{material.unit}
                                  </span>
                                  <span>Работа: {(material.labor_time * 60).toFixed(0)} мин</span>
                                </div>
                              </div>
                              
                              {inEstimate ? (
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    className="h-8 w-8 rounded-sm"
                                    onClick={() => {
                                      const index = estimateItems.findIndex(i => i.material_id === material.id);
                                      updateQuantity(index, inEstimate.quantity - 1);
                                    }}
                                    data-testid={`decrease-${material.id}`}
                                  >
                                    <Minus className="w-4 h-4" />
                                  </Button>
                                  <span className="w-12 text-center font-mono font-bold">
                                    {inEstimate.quantity}
                                  </span>
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    className="h-8 w-8 rounded-sm"
                                    onClick={() => {
                                      const index = estimateItems.findIndex(i => i.material_id === material.id);
                                      updateQuantity(index, inEstimate.quantity + 1);
                                    }}
                                    data-testid={`increase-${material.id}`}
                                  >
                                    <Plus className="w-4 h-4" />
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  size="sm"
                                  className="uppercase tracking-wider text-xs font-bold rounded-sm btn-press"
                                  onClick={() => addItem(material)}
                                  data-testid={`add-${material.id}`}
                                >
                                  <Plus className="w-4 h-4 mr-1" />
                                  Добавить
                                </Button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  </Card>
                </div>
              )}
            </div>

            {/* Estimate Sidebar */}
            <div className="space-y-4" data-testid="estimate-sidebar">
              <Card className="border-2 rounded-sm sticky top-20">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-bold uppercase tracking-wider">Смета</CardTitle>
                    {estimateItems.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearEstimate}
                        className="text-destructive hover:text-destructive"
                        data-testid="clear-estimate"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {estimateItems.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground">
                      <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p className="text-sm">Добавьте материалы для расчёта</p>
                    </div>
                  ) : (
                    <>
                      <ScrollArea className="h-[300px]">
                        <div className="divide-y">
                          {estimateItems.map((item, index) => {
                            const IconComponent = categoryIcons[item.category];
                            
                            return (
                              <div 
                                key={item.material_id}
                                className="estimate-row p-3"
                                data-testid={`estimate-item-${item.material_id}`}
                              >
                                <div className="flex items-start gap-2">
                                  <div className={`w-6 h-6 rounded-sm ${categoryColors[item.category]} flex items-center justify-center flex-shrink-0 mt-1`}>
                                    <IconComponent className="w-3 h-3 text-white" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate">{item.material_name}</p>
                                    <div className="flex items-center gap-2 mt-1">
                                      <Input
                                        type="number"
                                        value={item.quantity}
                                        onChange={(e) => updateQuantity(index, parseFloat(e.target.value) || 0)}
                                        className="w-16 h-7 text-xs font-mono text-center rounded-sm"
                                        min="0"
                                        step={item.unit === "м" ? "0.5" : "1"}
                                        data-testid={`quantity-input-${item.material_id}`}
                                      />
                                      <span className="text-xs text-muted-foreground">{item.unit}</span>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <p className="font-mono font-bold text-sm animate-count">
                                      {formatCurrency(item.total_cost)}
                                    </p>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6 text-muted-foreground hover:text-destructive"
                                      onClick={() => removeItem(index)}
                                      data-testid={`remove-${item.material_id}`}
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </ScrollArea>
                      
                      {/* Totals */}
                      <div className="border-t bg-secondary/30 p-4 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Материалы:</span>
                          <span className="font-mono font-bold" data-testid="total-materials">
                            {formatCurrency(totals.materials)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Работа ({laborRate}€/ч):</span>
                          <span className="font-mono font-bold" data-testid="total-labor">
                            {formatCurrency(totals.labor)}
                          </span>
                        </div>
                        <Separator />
                        <div className="flex justify-between items-center">
                          <span className="font-bold uppercase tracking-wider">Итого:</span>
                          <span className="text-2xl font-mono font-black text-primary animate-count" data-testid="grand-total">
                            {formatCurrency(totals.total)}
                          </span>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="p-4 space-y-2 border-t">
                        <Button 
                          className="w-full uppercase tracking-wider font-bold rounded-sm btn-press"
                          onClick={() => setSaveDialogOpen(true)}
                          data-testid="save-estimate-btn"
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Сохранить смету
                        </Button>
                        <Button 
                          variant="outline"
                          className="w-full uppercase tracking-wider font-bold rounded-sm btn-press"
                          onClick={exportPDF}
                          data-testid="export-pdf-btn"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Экспорт PDF
                        </Button>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          /* History View */
          <div data-testid="history-view">
            <h2 className="text-2xl font-bold mb-6 tracking-tight">История смет</h2>
            
            {estimates.length === 0 ? (
              <Card className="border-2 rounded-sm">
                <CardContent className="p-12 text-center">
                  <History className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h3 className="text-lg font-bold mb-2">Нет сохранённых смет</h3>
                  <p className="text-muted-foreground mb-4">Создайте смету в калькуляторе и сохраните её</p>
                  <Button onClick={() => setView("calculator")} data-testid="go-to-calculator">
                    <Calculator className="w-4 h-4 mr-2" />
                    Перейти к калькулятору
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {estimates.map((estimate) => (
                  <Card 
                    key={estimate.id}
                    className="history-card border-2 rounded-sm cursor-pointer"
                    onClick={() => setSelectedEstimate(estimate)}
                    data-testid={`history-item-${estimate.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-bold">{estimate.name}</h3>
                          <p className="text-xs text-muted-foreground">
                            {formatDate(estimate.created_at)}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          onClick={(e) => deleteEstimate(estimate.id, e)}
                          data-testid={`delete-estimate-${estimate.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Позиций:</span>
                          <span className="font-mono">{estimate.items.length}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Материалы:</span>
                          <span className="font-mono">{formatCurrency(estimate.total_materials)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Работа:</span>
                          <span className="font-mono">{formatCurrency(estimate.total_labor)}</span>
                        </div>
                        <Separator className="my-2" />
                        <div className="flex justify-between items-center">
                          <span className="font-bold">Итого:</span>
                          <span className="font-mono font-bold text-primary text-lg">
                            {formatCurrency(estimate.grand_total)}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Save Dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent className="rounded-sm" data-testid="save-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold uppercase tracking-wider">Сохранить смету</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="Название сметы (например: Квартира на ул. Мира)"
              value={estimateName}
              onChange={(e) => setEstimateName(e.target.value)}
              className="rounded-sm"
              data-testid="estimate-name-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)} className="rounded-sm">
              Отмена
            </Button>
            <Button onClick={saveEstimate} className="rounded-sm uppercase tracking-wider font-bold" data-testid="confirm-save-btn">
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Estimate Detail Dialog */}
      <Dialog open={!!selectedEstimate} onOpenChange={() => setSelectedEstimate(null)}>
        <DialogContent className="max-w-2xl rounded-sm" data-testid="estimate-detail-dialog">
          {selectedEstimate && (
            <>
              <DialogHeader>
                <DialogTitle className="font-bold">{selectedEstimate.name}</DialogTitle>
                <p className="text-sm text-muted-foreground">
                  {formatDate(selectedEstimate.created_at)} • Ставка: {selectedEstimate.labor_rate}€/ч
                </p>
              </DialogHeader>
              
              <ScrollArea className="max-h-[400px]">
                <table className="w-full text-sm">
                  <thead className="bg-secondary sticky top-0">
                    <tr>
                      <th className="text-left p-2 font-bold">Материал</th>
                      <th className="text-center p-2 font-bold">Кол-во</th>
                      <th className="text-right p-2 font-bold">Материал</th>
                      <th className="text-right p-2 font-bold">Работа</th>
                      <th className="text-right p-2 font-bold">Итого</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {selectedEstimate.items.map((item, idx) => (
                      <tr key={idx} className="hover:bg-secondary/50">
                        <td className="p-2">{item.material_name}</td>
                        <td className="p-2 text-center font-mono">{item.quantity} {item.unit}</td>
                        <td className="p-2 text-right font-mono">{formatCurrency(item.material_cost)}</td>
                        <td className="p-2 text-right font-mono">{formatCurrency(item.labor_cost)}</td>
                        <td className="p-2 text-right font-mono font-bold">{formatCurrency(item.total_cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-secondary font-bold">
                    <tr>
                      <td colSpan="2" className="p-2">ИТОГО</td>
                      <td className="p-2 text-right font-mono">{formatCurrency(selectedEstimate.total_materials)}</td>
                      <td className="p-2 text-right font-mono">{formatCurrency(selectedEstimate.total_labor)}</td>
                      <td className="p-2 text-right font-mono text-primary">{formatCurrency(selectedEstimate.grand_total)}</td>
                    </tr>
                  </tfoot>
                </table>
              </ScrollArea>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setSelectedEstimate(null)} className="rounded-sm">
                  Закрыть
                </Button>
                <Button onClick={() => loadEstimate(selectedEstimate)} className="rounded-sm uppercase tracking-wider font-bold" data-testid="load-estimate-btn">
                  Загрузить в калькулятор
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Print-only content */}
      <div className="hidden print:block p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-black">СМЕТА ЭЛЕКТРОМОНТАЖНЫХ РАБОТ</h1>
          <p className="text-muted-foreground">{new Date().toLocaleDateString('de-DE')}</p>
        </div>
        
        <table className="w-full border-collapse mb-8">
          <thead>
            <tr className="border-b-2">
              <th className="text-left p-2">Наименование</th>
              <th className="text-center p-2">Кол-во</th>
              <th className="text-right p-2">Цена</th>
              <th className="text-right p-2">Материал</th>
              <th className="text-right p-2">Работа</th>
              <th className="text-right p-2">Итого</th>
            </tr>
          </thead>
          <tbody>
            {estimateItems.map((item, idx) => (
              <tr key={idx} className="border-b">
                <td className="p-2">{item.material_name}</td>
                <td className="p-2 text-center">{item.quantity} {item.unit}</td>
                <td className="p-2 text-right">{formatCurrency(item.unit_price)}</td>
                <td className="p-2 text-right">{formatCurrency(item.material_cost)}</td>
                <td className="p-2 text-right">{formatCurrency(item.labor_cost)}</td>
                <td className="p-2 text-right font-bold">{formatCurrency(item.total_cost)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 font-bold">
              <td colSpan="3" className="p-2">ИТОГО</td>
              <td className="p-2 text-right">{formatCurrency(totals.materials)}</td>
              <td className="p-2 text-right">{formatCurrency(totals.labor)}</td>
              <td className="p-2 text-right text-xl">{formatCurrency(totals.total)}</td>
            </tr>
          </tfoot>
        </table>
        
        <div className="text-sm text-muted-foreground">
          <p>Ставка за работу: {laborRate}€/час</p>
        </div>
      </div>
    </div>
  );
}

export default App;
